import {IRegPointer, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {ControlArea, ControlAsyncArea, DateInputArea, IControlAreaContext, InputChoiceArea, LabelArea, StringInputArea} from "lib/commons/areas";
import {IFormJsonisable} from "lib/commons/forms";
import {IBasicUniversePointer, ICoreUniversePointer} from "lib/core/universe";
import {ERROR} from "lib/core/errorReport";
import {EUserAspects, EUserType, JUser, USER, UsersSrv} from "lib/core/user";
import {InputOrderedSetPanel, IOrderedSetEntry, OOrderedSetInit} from "back/commons/widgets/inputs";
import {IRoleEditUiHandlerContext, ORolesSelectorInit, RolesSelectorInput} from "lib/commons/roles";
import {CreateUserAction} from "back/core/actions/usersActions";
import {ActionBtn} from "back/commons/widgets/buttons";
import {ActionWrapper} from "lib/commons/actions";
import {OGridSmallInit} from "back/commons/widgets/grid-tags";
import {GridDataRowJson} from "back/commons/widgets/grid-libs";
import {IGridLineDrawer} from "back/commons/widgets/grid-core";
import {DOMSH} from "lib/commons/xml/domsh";
import {Box, OSkinableInit} from "back/commons/basis";
import "back/core/dialogs/dialogs_Perms";
import {UserSelfSrv} from "lib/core/userSelf";
import {IActionable} from "back/commons/actionables";

export interface IUserBaseAreaContext extends IControlAreaContext, IRegPointer<ICoreUniversePointer | IBasicUniversePointer> {
	/** Caractérise le contexte UI courant */
	uiContextSgn?: string
	/** Srvs de gestion de user associés à cette aréa */
	userSelfSrv: UserSelfSrv
	usersSrv?: UsersSrv
}

export interface IUserAreaContext extends IUserBaseAreaContext {
	/** User(s) sur lesquels s'appliquent cette area */
	users?: JUser[],
}

/** account */
export class UserAccountInputArea extends StringInputArea<IUserAreaContext> {
	constructor(id: string = "account") {
		super(id);
		this.setLabel("Compte");
		this.setPattern("[a-zA-Z0-9\\.\\-\\*_@]+");
		this.setRequired(true);
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
		this.setInputCustomValidity(async function (this: HTMLInputElement, ctx: IUserAreaContext): Promise<string> {
			if (!this.checkValidity()) return "Seuls les caractères alpha-numériques et \".-*_@\" sont autorisés";
			if (ctx.usersSrv && (ctx.usersSrv as UsersSrv).getUser) {
				if (this.value) {
					let checkedUser = await (ctx.usersSrv as UsersSrv).getUser(this.value);
					if (checkedUser) {
						let code = this.value;
						return `L'identifiant d'utilisateur '${code}' a déjà été utilisé.`;
					}
				}
			}
			return "";
		}, 500, true)
	}

	_buildControl(ctx: IUserAreaContext, name: string): HTMLInputElement {
		let input = super._buildControl(ctx, name);
		DOM.setAttr(input, "spellcheck", "false");
		DOM.setAttr(input, "autocomplete", "current-password");// "off" ne fonctionne pas
		return input;
	}
}

REG.reg.registerSvc('area.users.account', 1, new UserAccountInputArea());

/** account et propriétés */
class UsesPropertiesArea extends LabelArea<IUserAreaContext> {

	constructor() {
		super("account");
		this.setLabel("Compte");
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	_buildControl(ctx: IUserAreaContext, name: string): Box {
		let control = <Box î={{skinOver: "area-users-props-box"} as OSkinableInit}/> as Box;
		const DTFormatter = new Intl.DateTimeFormat("fr", {year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'});
		Object.assign(control, {
			fillJson(this: Box, parent: Jsonisable, root: Jsonisable): void {
				parent.account = this.dataset.account;
			},
			extractJson: function (this: Box, parent: JUser) {
				this.dataset.account = parent.account;
				let rootTag: HTMLDivElement = this.shadowRoot.querySelector("div");
				if (!rootTag) rootTag = this.shadowRoot.appendChild(<div/>) as HTMLDivElement;
				rootTag.innerHTML = "";
				let accountLine = rootTag.appendChild(<div/>);
				if (parent.isReadOnly)
					accountLine.appendChild(<span class="icon locked" title="Compte en lecture seule"/>);
				if (parent.isDisabled)
					accountLine.appendChild(<span class="icon disabled" title="Compte désactivé"/>);
				if (parent.isHidden && parent.userType === EUserType.user)
					accountLine.appendChild(<span class="icon system" title="Compte de service"/>);
				//accountLine.appendChild(<ButtonToggle onclick={function(this:ButtonToggle, ev:Event){this.toggleOn=!this.toggleOn}} î={{icon:"/@skin@/core/objects/user/system.svg", toggleOn:!parent.isHidden, uiContext:"bar"} as OButtonToggleInit}/>);
				accountLine.appendChild(
					<span class="account">{parent.account}</span>);
				if (parent.pwdDt) {
					let date = DTFormatter.format(parent.pwdDt);
					let txt = `Dernier changement de mot de passe : ${date}`;
					rootTag.appendChild(<div class="pwd">{txt}</div>);
				}
				return true;
			}
		} as IFormJsonisable);
		return control;
	}
}

REG.reg.registerSvc('area.users.props', 1, new UsesPropertiesArea());
REG.reg.registerSkin('area-users-props-box', 1, /* language=CSS */ `
	.icon {
		background-repeat: no-repeat;
		background-position: center;
		background-size: auto .9rem;
		padding-inline-start: 12px;
		margin-inline-end: 0.3em;
	}

	.disabled {
		background-image: url(/@skin@/core/objects/user/disabled.svg);
	}

	.locked {
		background-image: url(/@skin@/core/objects/user/locked.svg);
	}

	.system {
		background-image: url(/@skin@/core/objects/user/system.svg);
	}

	.account {
		font-weight: bold;
	}

	.pwd {
		font-size: .8rem;
	}
`);

/** firstName */
export class UserFirstNameInputArea extends StringInputArea<IUserAreaContext> {
	constructor(id: string = "firstName") {
		super(id);
		this.setLabel("Prénom");
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	_buildControl(ctx: IUserAreaContext, name: string): HTMLInputElement {
		let input = super._buildControl(ctx, name);
		DOM.setAttr(input, "spellcheck", "false");
		DOM.setAttr(input, "autocomplete", "current-password");// "off" ne fonctionne pas
		return input;
	}
}

REG.reg.registerSvc('area.users.firstName', 1, new UserFirstNameInputArea());

/** lastName */
export class UserLastNameInputArea extends StringInputArea<IUserAreaContext> {
	constructor(id: string = "lastName") {
		super(id);
		this.setLabel("Nom");
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	_buildControl(ctx: IUserAreaContext, name: string): HTMLInputElement {
		let input = super._buildControl(ctx, name);
		DOM.setAttr(input, "spellcheck", "false");
		DOM.setAttr(input, "autocomplete", "current-password");// "off" ne fonctionne pas
		return input;
	}
}

REG.reg.registerSvc('area.users.lastName', 1, new UserLastNameInputArea());


/** groups */
export class UserGroupsInputArea<C extends IUserAreaContext> extends ControlAsyncArea<C> implements IGridLineDrawer {
	constructor(id: string = "groups", public includeIsHiddenGroups: boolean | null = true, public checkGroupReentrancy = false, public loadDatasetOnInit = false) {
		super(id);
		this.setLabel("Groupes");
		this.setVisible((ctx) => {
			if (!ctx.usersSrv || !ctx.usersSrv.hasAspect(EUserAspects.groupable)) return false;
			return userAreaIsVisible.call(this, ctx);
		});
		this.setEnabled(userAreaIsEnabled);
	}

	async _loadControl(ctx: C, name: string): Promise<HTMLElement> {
		let inputBox = <div class="ctrlLbl"/>;
		// - Input
		const inputElt: InputOrderedSetPanel = inputBox.appendChild(new InputOrderedSetPanel().initialize({
			//init data déporté sur `extractJson` dans le cas de l'update de user (liste dépendant de la propriété 'flattenedGroups' du user courant)
			dataSet: this.loadDatasetOnInit ? await UserGroupsInputArea.fetchGroupsList(ctx, this.includeIsHiddenGroups) : {},
			selectionLabel: "Groupes sélectionnés",
			availableLabel: "Groupes disponibles",
			emptySelectionMsg: "Aucun",
			previewMode: "asLines",
			availableGridInit: {
				skinOver: "area-users-groups-tabs",
				lineDrawer: this,
			} as OGridSmallInit,
			selectionGridInit: {
				skinOver: "area-users-groups-tabs",
				lineDrawer: this,
			} as OGridSmallInit,
			name,
			checkValidity: this.checkGroupReentrancy ? function (this: InputOrderedSetPanel, selectedEntries) {
				// Contrôle de cycle dans les groupes via 'flattenedGroups'
				const incompatList: string[] = [];
				selectedEntries?.forEach(entry => {
					if (((inputElt.dataSet[entry] as any)?.flattenedGroups as string[])?.indexOf((this as any)._currentAccount) > -1)
						incompatList.push(inputElt.dataSet[entry].label)
				});
				if (incompatList.length) {
					const list: string = incompatList.join(", ");
					return `Groupe(s) incompatible(s) avec le groupe courant : '${list}'`
				} else
					return true;
			} : undefined,
		} as OOrderedSetInit));

		let addGroupBtn: IActionable<any>;

		const includeIsHiddenGroups = this.includeIsHiddenGroups;
		Object.assign(inputBox, {
			fillJson(this: Box, parent: Jsonisable, root: Jsonisable): void {
				parent[name] = inputElt.value;
			},
			extractJson: function (this: Box, parent: JUser) {
				// Recalcul du dataSet : les propriétés peuvent évoluer (modif du user courant, modif des 'flattenedGroups', ...)
				UserGroupsInputArea.fetchGroupsList(ctx, includeIsHiddenGroups).then((val) => {
					inputElt.dataSet = val;
				});
				(inputElt as any)._currentAccount = parent.account;
				inputElt.value = (parent as any)[name];
				if (addGroupBtn) addGroupBtn.disabled = parent.isReadOnly;
				return true;
			}
		} as IFormJsonisable);

		// maj de la zone input du formulaire "roles"
		inputElt.addEventListener("change", (evt) => {
			inputElt.form.querySelectorAll("c-input-roles-selector").forEach(async (elt: RolesSelectorInput) => {
				let inheritedRoles = await (ctx.usersSrv as UsersSrv).resolveRoles(inputElt.value);
				await elt.setInheritedRoles(inheritedRoles);
			});
			inputElt.reportValidity();
		});

		const defaultValue = this.getDefaultValue(ctx);
		if (defaultValue != null) inputElt.value = defaultValue;
		await this._initStdControl(inputElt, ctx);

		// - Btn add groupes
		let wrp = new ActionWrapper();
		wrp.setOverridenSvc(CreateUserAction.SINGLETON_group).setExecute(async (ctx: C, ev?: Event) => {
			let newGroup = await wrp.getWrapped(ctx).execute(ctx, ev);
			if (newGroup) {
				// FIXME : maj du dataset + ouverture du panel avec sélection de la nouvelle entrée "newGroup"
				inputElt.dataSet = await UserGroupsInputArea.fetchGroupsList(ctx, this.includeIsHiddenGroups);
			}
		});
		if (this.isAvailable(ctx)) addGroupBtn = inputBox.appendChild(ActionBtn.buildButton(wrp, ctx, "bar"));

		return inputBox;
	}

	static async fetchGroupsList(ctx: IUserAreaContext, includeIsHidden?: boolean | null): Promise<Dict<IOrderedSetEntry>> {
		const groupsSet = await (ctx.usersSrv as UsersSrv).list(null, EUserType.group, includeIsHidden, null, 100, null, null, ['flattenedGroups']);
		const groupsList = groupsSet.userList?.sort((groupA, groupB) => USER.getPrimaryName(groupA).localeCompare(USER.getPrimaryName(groupB)));
		let dataset: Dict<IOrderedSetEntry> = {};
		if (groupsList?.length > 0) {
			groupsList.forEach((pGroup) => {
				if (pGroup && (!ctx.users || !ctx.users.find((user) => user?.account === pGroup.account))) {
					const grps: string[] = pGroup.flattenedGroups?.filter(entry => entry != pGroup.account)
						.map(entry => {
							const userObj = groupsList.find((user => user.account == entry));
							return userObj ? USER.getPrimaryName(userObj) : entry;
						});

					dataset[pGroup.account] = {
						label: USER.getPrimaryName(pGroup),
						icon: "/@skin@/core/objects/group.svg",
						iconFilter: pGroup.isHidden ? "opacity(50%)" : undefined,
						description: grps?.length ? `groupes liés : ${grps}` : undefined,
						flattenedGroups: pGroup.flattenedGroups
					} as any;
				}
			});
		}
		return dataset;
	}

	redrawLine(row: GridDataRowJson<any>, line: HTMLElement): void {
		DOM.setAttrBool(line, "data-user-hidden", row.rowDatas.isHidden);
	}
}

REG.reg.registerSvc('area.users.groupsmembers.users', 1, new UserGroupsInputArea("groups", false, false));
REG.reg.registerSvc('area.users.groupsmembers.groups', 1, new UserGroupsInputArea("groups", null, true));
REG.reg.registerSvc('area.users.groupsmembers.users.create', 1, new UserGroupsInputArea("groups", false, false, true));
REG.reg.registerSvc('area.users.groupsmembers.groups.create', 1, new UserGroupsInputArea("groups", null, true, true));
REG.reg.registerSkin("area-users-groups-tabs", 1, /* language=CSS */ `
	.cell > .icon {
		padding-inline-start: 1.2em !important;
		margin-inline-end: 0.3em !important;
	}

	div[data-user-hidden] {
		font-style: italic;
	}
`);

/** Gestion des groupes sur n users sélectionnés */
export class UserGroupsMultiInputArea<C extends IUserAreaContext> extends ControlAsyncArea<C> implements IGridLineDrawer {
	constructor(id: string = "groups", public includeIsHidden: boolean | null = true) {
		super(id);
		this.setLabel("Groupes");
		this.setVisible((ctx) => {
			if (!ctx.usersSrv || !ctx.usersSrv.hasAspect(EUserAspects.groupable)) return false;
			return userAreaIsVisible.call(this, ctx);
		});
		this.setEnabled(userAreaIsEnabled);
	}

	async _loadControl(ctx: C, name: string): Promise<HTMLElement> {
		let inputBox = <div class="ctrlLbl"/>;
		// - Input
		const inputElt = inputBox.appendChild(new InputOrderedSetPanel().initialize({
			dataSet: await UserGroupsInputArea.fetchGroupsList(ctx, this.includeIsHidden),
			selectionLabel: "Groupes sélectionnés",
			availableLabel: "Groupes disponibles",
			emptySelectionMsg: "Aucun",
			availableGridInit: {
				skinOver: "area-users-groups-tabs",
				lineDrawer: this,
			} as OGridSmallInit,
			selectionGridInit: {
				skinOver: "area-users-groups-tabs",
				lineDrawer: this,
			} as OGridSmallInit,
			name
		} as OOrderedSetInit));
		const defaultValue = this.getDefaultValue(ctx);
		if (defaultValue != null) inputElt.value = defaultValue;
		// Gestion de l'état indeterminate
		inputElt.indeterminate = true;
		inputElt.addEventListener("change", function (this: InputOrderedSetPanel, evt) {
			this.indeterminate = false;
		});
		inputElt.addEventListener('keydown', async function (this: InputOrderedSetPanel, ev: KeyboardEvent) {
			if (!this.disabled && (ev.key === 'Delete' || ev.key === 'Backspace') && this === DOMSH.findDocumentOrShadowRoot(this).activeElement) {
				this.indeterminate = true;
				this.value = null;
				ev.stopPropagation();
				this.form.dispatchEvent(new CustomEvent("change", {bubbles: true, cancelable: false}));
				return true;
			}
			return false;
		});

		// - Btn rôles
		/*
		let wrp = new ActionWrapper();
		wrp.setOverridenSvc(CreateUserAction.SINGLETON_group).setExecute(async (ctx: C, ev?: Event) => {
			let newGroup = await wrp.getWrapped(ctx).execute(ctx, ev);
			if (newGroup) {
				// FIXME : maj du dataset + ouverture du panel avec sélection de la nouvelle entrée "newGroup"
				// inputElt.dataSet = await UserGroupsInputArea.fetchGroupsList(ctx, this.filterHiddenGroups);
			}
		});
		inputBox.appendChild(ActionBtn.buildButton(wrp, ctx, "bar"));
		 */
		return inputBox;
	}

	redrawLine(row: GridDataRowJson<any>, line: HTMLElement): void {
		DOM.setAttrBool(line, "data-user-hidden", row.rowDatas.isHidden);
	}
}

REG.reg.registerSvc('area.users.groupsmembers.multi.users', 1, new UserGroupsMultiInputArea("groups", false));

/** grantedRoles */
export class UserRolesInputArea extends LabelArea {

	constructor(id?: string, public fixedCtx?: ORolesSelectorInit & IRoleEditUiHandlerContext) {
		super(id || "rolesSet");
		if (!this.fixedCtx) this.fixedCtx = {
			name: this.getId(),
		} as ORolesSelectorInit & IRoleEditUiHandlerContext;
		this.setLabel("Rôles");
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	isVisible(ctx: IUserAreaContext): boolean {
		if (!ctx.usersSrv || !ctx.usersSrv.hasAspect(EUserAspects.rolable)) return false;
		return super.isVisible(ctx);
	}

	_buildControl(ctx: IUserAreaContext, name: string): HTMLElement {
		const elt = <RolesSelectorInput î={Object.assign(Object.assign({}, ctx), this.fixedCtx)}/> as RolesSelectorInput;
		this._initStdControl(elt, ctx);
		return elt;
	}

}

REG.reg.registerSvc('area.users.grantedRoles.users', 1, new UserRolesInputArea(null, {uiLayerSgn: 'users'} as ORolesSelectorInit & IRoleEditUiHandlerContext));
REG.reg.registerSvc('area.users.grantedRoles.groups', 1, new UserRolesInputArea(null, {uiLayerSgn: 'groups'} as ORolesSelectorInit & IRoleEditUiHandlerContext));

REG.reg.registerSvc('area.users.grantedRoles.multi.users', 1, new UserRolesInputArea(null, {uiLayerSgn: 'users', canBeIndeterminate: true} as ORolesSelectorInit & IRoleEditUiHandlerContext));
REG.reg.registerSvc('area.users.grantedRoles.multi.groups', 1, new UserRolesInputArea(null, {uiLayerSgn: 'groups', canBeIndeterminate: true} as ORolesSelectorInit & IRoleEditUiHandlerContext));

/** nickNames : gestion d'une *unique valeur* */
export class UserNicknamesFirstInputArea<C extends IUserAreaContext> extends StringInputArea<C> {
	constructor(id: string = "nickNames") {
		super(id);
		this.setLabel("Pseudo");
		this.setInputCustomValidity(async function (this: HTMLInputElement, ctx: IUserAreaContext): Promise<string> {
			if (this.value) {
				if (ctx.usersSrv && (ctx.usersSrv as UsersSrv).getUser) {
					if (ctx.users && ctx.users.length === 1 && ctx.users[0].nickNames && ctx.users[0].nickNames.indexOf(this.value) > -1)
						return "";
					let checkedUser = await (ctx.usersSrv as UsersSrv).getUser(this.value);
					if (checkedUser) {
						let code = this.value;
						return `L'identifiant d'utilisateur '${code}' a déjà été utilisé.`;
					}
				}
			}
			return "";
		}, 500, true)
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	isVisible(ctx: C): boolean {
		if (!ctx.userSelfSrv.hasAspect(EUserAspects.oneNickName)) return false;
		return super.isVisible(ctx);
	}

	_buildControl(ctx: C, name: string): HTMLInputElement {
		const control = super._buildControl(ctx, name);
		DOM.setAttr(control, "spellcheck", "false");
		DOM.setAttr(control, "autocomplete", "current-password");// "off" ne fonctionne pas
		Object.assign(control, {
			fillJson: function (this: HTMLInputElement, parent: Jsonisable, root: Jsonisable) {
				if (!this.disabled) parent[this.name] = this.value ? [this.value] : []; //Si disabled, nickName non éditable => on ne le retourne pas au serveur.
			},
			extractJson: function (this: HTMLInputElement, parent: Dict<any>) {
				const array = parent[this.name];
				if (array && array.length) {
					this.value = array[0];
					return true;
				} else
					this.value = null;//pour RAZ
				return false;
			}
		} as IFormJsonisable);
		return control;
	}
}

REG.reg.registerSvc('area.users.nickNames.first', 1, new UserNicknamesFirstInputArea());

/** mail */
export class UserEmailInputArea extends StringInputArea<IUserAreaContext> {
	constructor(id: string = "email") {
		super(id, "email");
		this.setLabel("E-mail");
		this.setPattern("^[a-zA-Z0-9_.-]+@[a-zA-Z0-9.-]+$");
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	_buildControl(ctx: IUserAreaContext, name: string): HTMLInputElement {
		const control = super._buildControl(ctx, name);
		DOM.setAttr(control, "autocomplete", "current-password");// "off" ne fonctionne pas
		return control;
	}
}

REG.reg.registerSvc('area.users.email.user', 1, new UserEmailInputArea());
REG.reg.registerSvc('area.users.email.group', 1, new UserEmailInputArea());

/** groupName */
export class UserGroupNameInputArea extends StringInputArea<IUserAreaContext> {
	constructor(id: string = "groupName") {
		super(id);
		this.setLabel("Intitulé");
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	_buildControl(ctx: IUserAreaContext, name: string): HTMLInputElement {
		let input = super._buildControl(ctx, name);
		DOM.setAttr(input, "spellcheck", "false");
		DOM.setAttr(input, "autocomplete", "current-password");// "off" ne fonctionne pas
		return input;
	}
}

REG.reg.registerSvc('area.users.groupName', 1, new UserGroupNameInputArea());

/** isHidden, isDisabled */
class UsersBoolPropsInputArea extends InputChoiceArea<IUserAreaContext> {
	constructor(id: string) {
		super(id);
		this.setDefaultValue("false");
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	isEnabled(ctx: IUserAreaContext): boolean {
		//XXX A revoir : isEnabled n'est pas réévalué au changement du user à afficher dans l'area.
		// if (this._id === "isDisabled") {
		// 	//On interdit le user de se désactiver lui-même.
		// 	const currentUser = ctx.reg.env.universe.auth.currentAccount;
		// 	if (ctx.users.find((u) => u.account === currentUser)) return false;
		// }
		return super.isEnabled(ctx);
	}

	buildBody(ctx: IUserAreaContext): HTMLElement {
		let elt = super.buildBody(ctx);
		if (this.getDescription(ctx)) elt.title = this.getDescription(ctx);
		return elt;
	}

	_buildControl(ctx: any, name: string): HTMLDivElement {
		const control = super._buildControl(ctx, name);
		Object.assign(control, {
			fillJson: function (this: HTMLDivElement, parent: Jsonisable, root: Jsonisable) {
				let input = this.querySelector("input:checked") as HTMLInputElement;
				if (input) parent[name] = input.value === "true";
			},
			extractJson: function (this: HTMLDivElement, parent: Dict<any>) {
				if (parent[name])
					(this.querySelector("input[value='true']") as HTMLInputElement).checked = true;
				else
					(this.querySelector("input[value='false']") as HTMLInputElement).checked = true;
				return true;
			}
		} as IFormJsonisable);
		return control;
	}
}

REG.reg.registerSvc('area.users.isHidden.groups', 1, new UsersBoolPropsInputArea("isHidden")
	.setLabel("Visibilité")
	.requireVisiblePerm("ui.usersMgr.admin.hiddenGroup")
	.setDataset({"false": {label: "globale"}, "true": {label: "gestion", description: "Groupe intermédiaire n'ayant pas vocation à être exploité en dehors de l'écran de gestion des groupes"}})
);
REG.reg.registerSvc('area.users.isHidden.users', 1, new UsersBoolPropsInputArea("isHidden")
	.setLabel("Compte de service")
	.requireVisiblePerm("ui.usersMgr.admin.hiddenUser")
	.setDescription("Compte de service : compte masqué en dehors de l'écran de gestion des utilisateurs")
	.setDataset({"false": {label: "non"}, "true": {label: "oui"}})
);


REG.reg.registerSvc('area.users.isDisabled.users', 1, new UsersBoolPropsInputArea("isDisabled")
	.setLabel("Compte actif")
	.setDataset({"false": {label: "actif"}, "true": {label: "inactif"}})
	// pas de désactivation du user courant
	.setEnabled(function (this: ControlArea<any>, ctx: IUserAreaContext) {
			if (userAreaIsEnabled.call(this, ctx) === false) return false;
			return ctx.users?.find(user =>
				user?.account === ctx.reg.env.universe.auth.currentUser.account) ? false : true
		}
	)
);

REG.reg.registerSvc('area.users.enabledEndDt', 1, new DateInputArea("enabledEndDt").setLabel("Inactif à partir du")
	// pas de désactivation du user courant
	.setEnabled(function (this: ControlArea<any>, ctx: IUserAreaContext) {
			if (userAreaIsEnabled.call(this, ctx) === false) return false;
			return ctx.users?.find(user =>
				user?.account === ctx.reg.env.universe.auth.currentUser.account) ? false : true
		}
	)
);

/**
 * Area de construction d'un double champs pour entrer un mot de passe
 */
export class PasswordInputArea<C extends IUserAreaContext> extends StringInputArea<C> {
	constructor(id?: string, public showLabelMode?: "placeholder") {
		super(id, "password");
		this.setLabel("Mot de passe");
		this.setVisible(userAreaIsVisible);
		this.setEnabled(userAreaIsEnabled);
	}

	buildBody(ctx: C): HTMLElement {
		const ctn = <div/>;
		if (!this.isVisible(ctx)) ctn.setAttribute('hidden', '');

		const pwdInput = this._buildControl(ctx, this.getId());
		DOM.setAttr(pwdInput, "autocomplete", "current-password");// "off" ne fonctionne pas
		if (this.showLabelMode === "placeholder") pwdInput.placeholder = this.getLabel(ctx);
		ctn.appendChild((this.showLabelMode !== "placeholder" && ctx.buildControlLabel) ? this._buildControlLabel(pwdInput, ctx) : <div class="ctrlLbl">{pwdInput}</div>);
		this._initStdControl(pwdInput, ctx);

		let pwdValidRequest = 0;
		if (this.isAvailable(ctx)) {
			pwdInput.addEventListener('input', () => {
				if (pwdValidRequest) {
					clearTimeout(pwdValidRequest);
					pwdValidRequest = 0;
				}
				pwdValidRequest = setTimeout(async () => {
					await this._checkPwd(pwdInput);
				}, 500) as any;
			});

			const confirmInput = this._buildControl(ctx, "confirmPwd");
			DOM.setAttr(confirmInput, "autocomplete", "current-password");
			this._initStdControl(confirmInput, ctx);
			if (this.showLabelMode === "placeholder") confirmInput.placeholder = "Confirmation";
			ctn.appendChild((this.showLabelMode !== "placeholder" && ctx.buildControlLabel) ? this._buildControlLabel(confirmInput, ctx, "Confirmation") : <div class="ctrlLbl">{confirmInput}</div>);

			Object.assign(confirmInput, {
				extractJson: function (this: HTMLInputElement, parent: Jsonisable) {
					//input technique (ni récupéré, ni renvoyé au serveur)
				},
				fillJson: function (this: HTMLDivElement, parent: Jsonisable, root: Jsonisable) {
					//input technique (ni récupéré, ni renvoyé au serveur)
				}
			});

			let confirmValidRequest = 0;
			confirmInput.addEventListener('input', () => {
				if (confirmValidRequest) {
					clearTimeout(confirmValidRequest);
					confirmValidRequest = 0;
				}
				confirmValidRequest = setTimeout(() => {
					if (confirmInput.value && confirmInput.value != pwdInput.value) confirmInput.setCustomValidity("Les mots de passe ne correspondent pas.");
					else confirmInput.setCustomValidity('');
				}, 300) as any;
			});

			pwdInput.addEventListener('input', () => {
				confirmInput.dispatchEvent(new CustomEvent("input", {bubbles: true, cancelable: false}));
			});
		}
		return ctn;
	}

	async _checkPwd(input: HTMLInputElement) {
		input.setCustomValidity('')
		if (input.value && input.checkValidity()) {
			const reg = REG.findReg<IBasicUniversePointer>(input);
			const resp = await reg.env.universe.userSelf.checkPwd({password: input.value});

			if (resp.result === "ok") {
				input.setCustomValidity('');
			} else if (resp.result == "failedInvalidDatas" && resp.secondaryResults && resp.secondaryResults.msgs) {
				input.setCustomValidity(resp.secondaryResults.msgs[0]);
			} else {
				ERROR.reportError({
					msg: "Une erreur est survenue lors de la vérification du mot de passe.",
					ctx: {reg},
					adminDetails: JSON.stringify(resp)
				});
			}
		}
	}
}

/** Hook sur le isVisible des areas de users */
export type IUserAreaIsVisibleHook = (this: ControlArea<any>, ctx: IUserBaseAreaContext) => boolean;

/** @return false si masqué
 * ctx "IUserBaseAreaContext" non utilisé car nécessite un recalcul du form lors de chaque sélection de user dans usersMgr.
 * A activer si le besoin apparait (init form userAreas dépendant de la liste des users)
 */
export function userAreaIsVisible(this: ControlArea<any>, ctx: IUserBaseAreaContext): boolean {
	let hook = ctx.reg.getSvc("user.area.visible.hook") as IUserAreaIsVisibleHook;
	if (hook) return hook.call(this, ctx);
	return null;
}

/** Hook sur le isEnabled des areas de users */
export type IUserAreaIsEnabledHook = (this: ControlArea<any>, ctx: IUserBaseAreaContext) => boolean;

/** @return false si disabled
 * ctx "IUserBaseAreaContext" non utilisé car nécessite un recalcul du form lors de chaque sélection de user dans usersMgr.
 * A activer si le besoin apparait (init form userAreas dépendant de la liste des users)
 */
export function userAreaIsEnabled(this: ControlArea<any>, ctx: IUserBaseAreaContext): boolean {
	let hook = ctx.reg.getSvc("user.area.enabled.hook") as IUserAreaIsEnabledHook;
	if (hook) return hook.call(this, ctx);
	return null;
}
